import { useMutation, useQueryClient } from '@tanstack/react-query';

/* APIS */
import { cart } from '@api/apis';

import { cartQueryKeys } from './queries.keys';

/* Notification Store */
import { NoticeTypes, updateAppNotification } from '@store/client/store.notifications';

/* hooks */
import useNotice from '@hooks/useNotice';

/* utils */
import { Logger } from '@utils/logger';

/* Tracking */
import { SegmentTrackingAction } from '@integrations/segment/checkout';

/* types */
import { deriveResponseError } from '@utils/object';
import { NextRouter } from 'next/router';
import { checkoutVerificationPath, signInPath } from 'src/constants/paths';
import { BOOKING_STAGES } from 'src/constants/cart';

type TUseCartStartBookingProp = {
  errorType?: NoticeTypes;
};

/** Handles onSuccess for useCartMutationStartBooking - SINGLE CART ONLY */
export const handleSingleServiceBookingSuccess = ({ router, isPasswordless }: { router: NextRouter; isPasswordless: boolean }) => {
  /* The intent here is to skip the cart page, and instead go straight to the checkout
   * verification page (if passwordless auth), or to the sign in page (if not).
   * Single-service carts should never go to the cart page.
   */
  if (isPasswordless) {
    router.push(checkoutVerificationPath());
  } else {
    router.push({
      pathname: signInPath(),
      search: `?redirectTo=${BOOKING_STAGES.VERIFICATION}`,
    });
  }
};
export default function useCartMutationStartBooking({ errorType = NoticeTypes.MODAL_NOTICE }: TUseCartStartBookingProp = {}) {
  const queryClient = useQueryClient();
  const { error } = useNotice();

  return useMutation({
    mutationFn: async () => {
      const data = await cart.startBooking();
      if (data.status === 500 || data.err) throw data;

      return data;
    },
    onSuccess: data => {
      const { cart: cartResponse = {} } = data?.data ?? {};
      queryClient.setQueryData(cartQueryKeys.all, { ...cartResponse });

      SegmentTrackingAction.checkoutStarted(cartResponse);
    },
    onError: data => {
      /* Do we want a snack or modal errorType */
      if (errorType === NoticeTypes.MODAL_NOTICE) {
        const { errors = 'Could not add your selection(s).', error_title: errorTitle = 'Start Booking Error' } = deriveResponseError(data);

        updateAppNotification({ error_title: errorTitle, errors });
      } else {
        error('Error! Could not add your selection.');
      }

      Logger('useCartAddToCart')(data as Error);
    },
  });
}
